import React, { useEffect, useState } from "react";
import { Card, Col, Container, Form, Row } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { format, parseISO } from "date-fns";
import usePostRequest from "../../hooks/usePostRequest";
import { API_ENDPOINTS, aspetto_logo } from "../../constant/_constant";
import "./OrderStatus.css";

const formatDueDate = (value) => {
  if (!value) return "";
  try {
    return format(parseISO(value), "MMM d, yyyy");
  } catch {
    return value;
  }
};

/* Public (no login) page: customers look up limited order info by order number. */
function OrderStatus() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [orderNumber, setOrderNumber] = useState(orderId || "");
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const { postData, isLoading } = usePostRequest(API_ENDPOINTS.PUBLIC_ORDER_STATUS);

  const lookup = (value) => {
    const trimmed = value.trim();
    if (!trimmed) {
      setResult(null);
      setErrorMessage("Please enter an order number.");
      return;
    }
    postData(
      { order_id: trimmed },
      (response) => {
        setResult(response?.data || null);
        setErrorMessage("");
      },
      (error) => {
        setResult(null);
        setErrorMessage(error?.message || "Something went wrong. Please try again later.");
      }
    );
  };

  useEffect(() => {
    if (orderId) lookup(orderId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = orderNumber.trim();
    if (trimmed && trimmed !== orderId) {
      // Shareable URL; the effect above runs the lookup
      navigate(`/order-status/${encodeURIComponent(trimmed)}`);
    } else {
      lookup(orderNumber);
    }
  };

  const rows = result
    ? [
        { label: "Order number", value: result.order_id },
        { label: "Customer", value: result.customer_name },
        { label: "Current stage", value: result.current_stage },
        { label: "Next stage", value: result.next_stage },
        { label: "Estimated due date", value: formatDueDate(result.estimated_date) },
      ]
    : [];

  return (
    <div className="order_status_page">
      <Container>
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card className="order_status_card">
              <Card.Body>
                <div className="text-center mb-3">
                  <img src={aspetto_logo} height={70} alt="Aspetto" />
                </div>
                <h1 className="order_status_title text-center">Check your order status</h1>
                <p className="text-center text-muted mb-4">
                  Enter the order number from your order documents.
                </p>

                <Form onSubmit={handleSubmit} noValidate>
                  <Form.Label htmlFor="order-number">Order number</Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Control
                      id="order-number"
                      value={orderNumber}
                      onChange={(e) => setOrderNumber(e.target.value)}
                      placeholder="e.g. 47QDCC26MEVW2"
                      maxLength={100}
                      autoFocus
                    />
                    <button
                      type="submit"
                      className={`btn btn-theme ${isLoading ? "btn-loading" : ""}`}
                      disabled={isLoading}
                    >
                      Check status
                    </button>
                  </div>
                </Form>

                {errorMessage && (
                  <div className="alert alert-danger mt-4 mb-0" role="alert">
                    {errorMessage}
                  </div>
                )}

                {result && (
                  <dl className="order_status_result mt-4 mb-0">
                    {rows.map((row) => (
                      <div className="order_status_row" key={row.label}>
                        <dt>{row.label}</dt>
                        <dd>{row.value || "—"}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default OrderStatus;